import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import InboxIcon from "@mui/icons-material/Inbox";
import { MemberInfo } from "../Creation/CreateGroupForm";
import { ImageAvatar } from "./ImageAvatar";

interface UserListModalProps {
  open: boolean;
  onClose: () => void;
  members: MemberInfo[];
  onDelete: (id: string) => void;
}

function UserListModal({ open, onClose, members, onDelete }: UserListModalProps) {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="user-management-modal-title"
      PaperProps={{
        sx: {
          width: 480,
          maxWidth: "92vw",
          borderRadius: 4,
          boxShadow: "0 8px 40px rgba(16,24,64,0.14), 0 2px 12px rgba(16,24,64,0.08)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 3,
          py: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <GroupOutlinedIcon sx={{ fontSize: "1.1rem", color: "primary.main" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            id="user-management-modal-title"
            sx={{ fontSize: "1rem", fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}
          >
            Circle Members
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {members.length} {members.length === 1 ? "member" : "members"} added
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{
            color: "text.secondary",
            "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
          }}
        >
          <CloseIcon sx={{ fontSize: "1.1rem" }} />
        </IconButton>
      </Box>

      {/* Member list */}
      <Box sx={{ maxHeight: 380, overflow: "auto", py: 1 }}>
        {members.length === 0 ? (
          <Box
            sx={{
              py: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: "action.hover",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <InboxIcon sx={{ fontSize: "1.25rem", color: "text.disabled" }} />
            </Box>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.secondary" }}>
              No members added yet.
            </Typography>
          </Box>
        ) : (
          members.map((user, index) => (
            <React.Fragment key={user.id}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  px: 3,
                  py: 1.5,
                  transition: "background-color 0.12s",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                }}
              >
                <ImageAvatar
                  displayImg={user.displayImg ?? undefined}
                  firstName={user.firstName}
                  sx={{ width: 36, height: 36, fontSize: "0.82rem" }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.88rem",
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.firstName} {user.lastName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.email}
                  </Typography>
                </Box>
                <IconButton
                  aria-label="remove member"
                  onClick={() => onDelete(user.id)}
                  size="small"
                  sx={{
                    color: "text.disabled",
                    "&:hover": {
                      color: "error.main",
                      bgcolor: alpha(theme.palette.error.main, 0.06),
                    },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Box>
              {index < members.length - 1 && <Divider sx={{ ml: 8.5 }} />}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontSize: "0.82rem",
            fontWeight: 600,
            borderRadius: "8px",
            textTransform: "none",
            color: "text.secondary",
            "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
          }}
        >
          Done
        </Button>
      </Box>
    </Dialog>
  );
}

export default UserListModal;
